import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { CartModel, PromotionModel, BillToModel, ShipToModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { getDataLayer } from "@MasterBluePrint/Tracking/trackingHelper";
import { EnhancedTransactionData } from "../EnhancedTransactionData";

function getGtag() {
    return (window as any).gtag;
}

function getPromotionCode(cartLine: CartLineModel, reviewAndPayPromotions: PromotionModel[] | undefined): string {
    const cartLinePromo = reviewAndPayPromotions?.filter(
        promo =>
            promo.orderLineId === cartLine.id,
    )[0];

    if (!cartLinePromo) {
        return "";
    }
    return cartLinePromo.promotionCode;
}

function trackEnhancedOrder(cart: CartModel, promos: PromotionModel[] | undefined, billTo?: BillToModel, datedPromoSetting?: string) {
    const gtag = getGtag();
    if (!gtag) {
        return;
    }

    const data: EnhancedTransactionData = {
        transaction_id: cart.orderNumber,
        value: cart.orderGrandTotal,
        tax: cart.totalTax,
        shipping: cart.shippingAndHandling,
        currency: "USD",
        coupon: datedPromoSetting,
        items: [],
    };

    cart.cartLines?.forEach((cartLine, index) => {
        let discountAmount = 0;
        if (cartLine.pricing?.unitListPrice && cartLine.pricing?.unitNetPrice) {
            discountAmount = cartLine.pricing?.unitListPrice - cartLine.pricing?.unitNetPrice;
        }

        data.items.push({
            affiliation: billTo?.companyName,
            coupon: getPromotionCode(cartLine, promos),
            discount: Math.round(discountAmount * 100) / 100,
            index,
            item_brand: cartLine.brand?.name,
            item_id: cartLine.erpNumber,
            item_name: cartLine.shortDescription,
            price: cartLine.pricing?.unitNetPrice,
            quantity: cartLine.qtyOrdered as number,
        });
    });

    gtag("event", "purchase", {
        transaction_id: data.transaction_id,
        value: data.value,
        tax: data.tax,
        shipping: data.shipping,
        currency: data.currency,
        coupon: data.coupon,
        items: data.items,
    });
}

export default function trackCompletedOrder(cart: CartModel, state: ApplicationState, cartToUpdate?: CartModel, shipTo?: ShipToModel, billTo?: BillToModel) {
    if (IS_SERVER_SIDE) {
        return;
    }

    const promos = getReviewAndPayPromotions(state).value;
    const datedPromoSetting = promos && promos[0] && promos[0].promotionCode !== null && promos[0].promotionCode !== "" ? promos[0].promotionCode : cart.promotionCode;

    // Google enhanced eCommerce
    trackEnhancedOrder(cart, promos, billTo, datedPromoSetting);

    const dataLayer = getDataLayer();
    dataLayer.push({ ecommerce: null });
    const dataLayerProducts: any[] = [];

    cartToUpdate?.cartLines?.forEach(cartLine => {
        let productCondition = "";
        if (cartLine.productId) {
            const productData = getProductState(state, cartLine.productId).value;
            productCondition = productData?.properties?.condition || cartLine?.properties?.cartLineCondition;
        }
        let discountAmount = 0;
        if (cartLine.pricing?.unitListPrice && cartLine.pricing?.unitNetPrice) {
            discountAmount = cartLine.pricing?.unitListPrice - cartLine.pricing?.unitNetPrice;
        }
        dataLayerProducts.push({
            affiliation: billTo?.companyName,
            coupon: getPromotionCode(cartLine, promos),
            price: cartLine?.pricing?.unitNetPrice ?? "",
            discount: Math.round(discountAmount * 100) / 100,
            id: cartLine.erpNumber,
            name: cartLine.shortDescription,
            brand: cartLine?.brand?.name,
            condition: productCondition,
            category: cartLine.properties["cartlineCategoryName"],
            quantity: cartLine.qtyOrdered,
            list: cartLine?.properties?.productSourceActionField || "",
            listid: cartLine?.properties?.listIdField || "",
            position: cartLine.properties["productListPosition"],
        });
    });
    const isGuestOrderData = cart?.properties?.isGuestOrderData;
    const customerEmailForLoggedinUse = cart?.initiatedByUserName;
    const customerEmailData = isGuestOrderData === 'No' ? customerEmailForLoggedinUse : shipTo?.email;
    const data = {
        event: "transactionComplete",
        purchase: {
            actionField: {
                id: cart.orderNumber,
                affiliation: "Standard Checkout",
                revenue: cart.orderGrandTotal,
                tax: cart.totalTax,
                shipping: cart.shippingAndHandling,
                coupon: datedPromoSetting,
                checkoutOption: {
                    actionField: { option: { shipping_method: cart.shipVia?.description } },
                },
                action: "purchase",
                isGuestCheckout: isGuestOrderData,
                customerEmail: customerEmailData
            },
            products: dataLayerProducts,
        },
    };

    dataLayer.push(data);
}
